import { lazy, Suspense } from 'react';
import { SharedModalOverlay } from '@/components/SharedModalOverlay';
import { UpdateAvailableCallout } from '@/components/UpdateAvailableCallout';
import { useCurrentUserContext } from '@/contexts';
import { RenderIf } from './RenderIf';

// Lazy: these pull in the Schedule/Dashboard feature's shared module graph
// (see Sentry WEB-CLIENT-284 et al, "Large Render Blocking Asset"). Statically
// importing them here — even though RenderIf/isAuthenticated keep them from
// ever mounting on most routes — put that whole graph in the eagerly
// modulepreloaded entry bundle for every route, including anonymous /page/*
// visitors who never touch Schedule or Dashboard.
const CreateScheduleModal = lazy(() =>
  import('@/pages/Dashboard/components/CreateScheduleModal').then((m) => ({
    default: m.CreateScheduleModal,
  }))
);
const SnippetIngredientInUseModal = lazy(() =>
  import('@/pages/Schedule/components/SnippetIngredientInUseModal').then(
    (m) => ({ default: m.SnippetIngredientInUseModal })
  )
);

// Same reasoning, and the same fix, for the four modals below (Sentry
// WEB-CLIENT-2A1 / 2A7 / 2A4 / 2A9): each was still a static import, so its
// component tree rode along in the eagerly modulepreloaded chunk that
// `useCurrentUserContext` anchors — the one every route pays for on first
// paint. None of the four render on an anonymous /page/* visit (isAuthenticated
// gates two, RegisterModal and UpgradeModal only ever open in response to a
// user action), so there is no reason their code should ship before that
// action happens.
const SettingsModal = lazy(() =>
  import('@/components/SettingsModal').then((m) => ({
    default: m.SettingsModal,
  }))
);
const PendingInvitationsModal = lazy(() =>
  import('@/components/PendingInvitationsModal').then((m) => ({
    default: m.PendingInvitationsModal,
  }))
);
const RegisterModal = lazy(() =>
  import('@/components/RegisterModal').then((m) => ({
    default: m.RegisterModal,
  }))
);
const UpgradeModal = lazy(() =>
  import('@/features/billing/components/UpgradeModal').then((m) => ({
    default: m.UpgradeModal,
  }))
);

export const GlobalComponents = () => {
  const { isAuthenticated } = useCurrentUserContext();

  return (
    <>
      {isAuthenticated && (
        <>
          <Suspense fallback={null}>
            <SnippetIngredientInUseModal />
          </Suspense>
          <Suspense fallback={null}>
            <SettingsModal />
          </Suspense>
          <Suspense fallback={null}>
            <PendingInvitationsModal />
          </Suspense>
        </>
      )}

      <RenderIf paths={['/dashboard', '/schedule/*']}>
        <Suspense fallback={null}>
          <CreateScheduleModal />
        </Suspense>
        <SharedModalOverlay />
      </RenderIf>

      <UpdateAvailableCallout />

      {/**
       * This needs to be rendered outside of an isAuthenticated check
       * so it doesn't unmount before consumers are notified of
       * successful authentication
       */}
      <Suspense fallback={null}>
        <RegisterModal />
      </Suspense>
      <Suspense fallback={null}>
        <UpgradeModal />
      </Suspense>
    </>
  );
};
